import React, { useState, useCallback, useMemo } from 'react';
import {
  Button, Typography, Select, Input, message, Upload, Spin,
} from 'antd';

import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import { addAccount, editAccount, removeAccount, resetPassword } from '../../Modules/Account/Presentation/actions';
import { selectAccounts } from '../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../Modules/User/Presentation/selectors';

import { Account, AccountLevel } from '../../Modules/Account/Model/Account';
import { CreateAccountDTO } from '../../Modules/Account/Domain/Repositories/AccountRepository';

import AccountTable from './components/AccountTable';
import AccountAddModal from './components/AccountAddModal';
import AccountEditModal from './components/AccountEditModal';
import PasswordResetModal from './components/PasswordResetModal';

import { canView, creatableLevels, levelKor } from '../../Utils/permission';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const levels: AccountLevel[] = ['ADMIN', 'DISTRIBUTOR', 'AGENCY', 'SELLER'];

const AccountsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const allAccounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);

  /* local UI state */
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [selected, setSelected] = useState<Account | null>(null);
  const [resetTarget, setResetTarget] = useState<Account | null>(null);

  const [gradeFilter, setGradeFilter] = useState<AccountLevel | ''>('');
  const [keyword, setKeyword] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  /* ---- 필터링 / 페이지 ---- */
  const filtered = useMemo(() => {
    return allAccounts
      .filter(acc => currentUser && canView(currentUser, acc))
      .filter(acc => !gradeFilter || acc.grade === gradeFilter)
      .filter(acc =>
        keyword
          ? [acc.username, acc.name, acc.memo ?? '']
            .some(f => f.toLowerCase().includes(keyword.toLowerCase()))
          : true,
      );
  }, [allAccounts, currentUser, gradeFilter, keyword]);

  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page, perPage]);

  /* ---- 콜백 ---- */

  const handleAdd = useCallback(
    (dto: CreateAccountDTO) => {
      dispatch(addAccount(dto))
        .unwrap()
        .then(() => {
          message.success('새로운 계정 생성이 완료되었습니다.');
          setAddOpen(false);
        })
        .catch((err: any) => {
          message.error(typeof err === 'string' ? err : (err?.message || '계정 생성에 실패했습니다.'));
        });
    },
    [dispatch],
  );

  const handleEditSubmit = useCallback(
    (dto: Partial<CreateAccountDTO>) => {
      if (!selected) return;
      if (!currentUser) return;
      dispatch(editAccount({ id: selected.id, dto, me: currentUser }))
        .unwrap()
        .then(() => { message.success('수정 완료되었습니다.'); setEditOpen(false); setSelected(null); })
        .catch((err: any) => {
          message.error(typeof err === 'string' ? err : (err?.message || '수정 실패하였습니다.'));
        });
    },
    [dispatch, selected, currentUser],
  );

  const handleDelete = useCallback(
    (id: number) => {
      dispatch(removeAccount(id))
        .unwrap()
        .then(() => message.success('삭제 완료되었습니다.'))
        .catch((err: any) => {
          message.error(typeof err === 'string' ? err : (err?.message || '삭제 실패하였습니다.'));
        });
    },
    [dispatch],
  );

  const handleResetPassword = useCallback(
    (account: Account) => {
      setResetTarget(account);
      setPasswordResetOpen(true);
    },
    [],
  );

  const handleResetPasswordSubmit = useCallback(
    (newPassword: string) => {
      if (!resetTarget) return;
      dispatch(resetPassword({ id: resetTarget.id, newPassword }))
        .unwrap()
        .then(() => {
          message.success('비밀번호가 재설정되었습니다.');
        })
        .catch((err: any) => {
          message.error(typeof err === 'string' ? err : (err?.message || '비밀번호 재설정에 실패했습니다.'));
        })
        .finally(() => {
          setPasswordResetOpen(false);
          setResetTarget(null);
        });
    },
    [dispatch, resetTarget],
  );

  const handleResetPasswordClose = useCallback(() => {
    setPasswordResetOpen(false);
    setResetTarget(null);
  }, []);

  if (!currentUser) {
    return <Spin style={{ display: 'block', margin: '120px auto' }} />;
  }

  return (
    <div>
      <Title level={3}>사용자 리스트</Title>

      {/* ---- 검색 영역 ---- */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
        <Select
          placeholder="회원등급"
          allowClear
          value={gradeFilter}
          onChange={v => { setGradeFilter(v as AccountLevel); setPage(1); }}
          style={{ width: 200 }}
        >
          <Option value="">전체</Option>
          {levels.map(l => (
            <Option key={l} value={l}>{levelKor[l]}</Option>
          ))}
        </Select>
        <Search
          placeholder="아이디, 이름, 메모"
          allowClear
          onSearch={v => { setKeyword(v); setPage(1); }}
          style={{ width: 300 }}
        />
      </div>

      {/* ---- 상단 툴바 ---- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Select
          value={perPage}
          style={{ width: 120 }}
          onChange={v => { setPerPage(v); setPage(1); }}
        >
          {[10, 30, 50, 100].map(n => <Option key={n} value={n}>{n}개씩</Option>)}
        </Select>

        <div>
          {creatableLevels(currentUser.grade).length > 0 && (
            <Button type="primary" style={{ marginLeft: 8 }} onClick={() => setAddOpen(true)}>
              계정추가
            </Button>
          )}
        </div>
      </div>

      {/* ---- 테이블 ---- */}
      <AccountTable
        currentUser={currentUser}
        data={paged}
        total={filtered.length}
        perPage={perPage}
        currentPage={page}
        onPageChange={setPage}
        onEdit={acc => { setSelected(acc); setEditOpen(true); }}
        onDelete={handleDelete}
        onResetPassword={handleResetPassword}
      />

      {/* ---- 모달 ---- */}
      <AccountAddModal
        currentUserGrade={currentUser.grade}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAdd}
      />
      <AccountEditModal
        currentUserGrade={currentUser.grade}
        open={editOpen}
        account={selected}
        onClose={() => { setEditOpen(false); setSelected(null); }}
        onSubmit={handleEditSubmit}
      />
      <PasswordResetModal
        open={passwordResetOpen}
        onClose={handleResetPasswordClose}
        onSubmit={handleResetPasswordSubmit}
        accountName={resetTarget ? `${resetTarget.username} (${resetTarget.name})` : undefined}
      />
    </div>
  );
};

export default AccountsPage;
